import http from "./httpService";
import { StreamApiBase } from "../utils/constants";
import auth, { getCurrentUser } from "./authService";
import { HydrovacTicket } from "../interface/api/HydrovacTicket";
import { GenerateTicketsPayload } from "../interface/api/GenerateTicketsPayload";
import { GenerateExcelExportPayload } from "../interface/api/GenerateExcelExportPayload";
import { SearchPayload } from "../interface/api/SearchPayload";
import { BulkSalePricePayload } from "../interface/api/BulkSalePricePayload";
import { HydrovacBulkUpdatePayload } from "../interface/api/HydrovacBulkUpdatePayload";
import { HydrovacBulkClearPayload } from "../interface/api/HydrovacBulkClearPayload";
import { GenerateReportPayload } from "../interface/api/GenerateReportPayload";

http.setJwt(auth.getJwt());

const BASE = StreamApiBase["hydrovac"];

export const getAll = () => {
  return http.get(`${BASE}/all`);
};

export const getLatest = () => {
  return http.get(`${BASE}/latest`);
};

// Today's received loads (full table) for the Received toggle.
export const getReceivedToday = () => {
  return http.get(`${BASE}/received`);
};

// Today's active loads (loaded today, not received) for the Active toggle.
export const getActiveToday = () => {
  return http.get(`${BASE}/active`);
};

// All completed tickets regardless of date, for the "Complete" dashboard filter.
export const getAllComplete = () => {
  return http.get(`${BASE}/complete`);
};

// All-time complete count for the dashboard chip.
export const getCompleteCount = (isBSeries?: boolean) => {
  return http.get(
    `${BASE}/complete/count`,
    isBSeries === undefined ? undefined : { isBSeries }
  );
};

export const getTicket = (id: number | string) => {
  const jwt = getCurrentUser();
  if (jwt) {
    return http.get(`${BASE}/${id}`);
  } else {
    const urlParams = new URLSearchParams(window.location.search);
    const hash = urlParams.get("hash");
    return http.get(`${BASE}/${id}/${hash}`);
  }
};

export const getTicketHash = (id: number) => {
  return http.get(`${BASE}/${id}/hash`);
};

export const generateTickets = (payload: GenerateTicketsPayload) => {
  return http.post(`${BASE}/generate`, payload);
};

export const driverSubmit = (id: number, submission: HydrovacTicket) => {
  const urlParams = new URLSearchParams(window.location.search);
  const hash = urlParams.get("hash");
  return http.post(`${BASE}/${id}/${hash}/driver-submit`, submission);
};

export const receiveTicket = (id: number) => {
  return http.post(`${BASE}/receive/${id}`, {});
};

export const clearHeadspace = (id: number, submission: HydrovacTicket) => {
  return http.post(`${BASE}/clear-headspace/${id}`, submission);
};

export const searchTickets = (payload: SearchPayload) => {
  return http.post(`${BASE}/search`, payload);
};

export const getPendingHeadspace = () => {
  return http.get(`${BASE}/pending-headspace`);
};

export const saveTicket = (ticket: HydrovacTicket) => {
  return http.put(`${BASE}/edit`, ticket);
};

export const dispatchTicket = (id: number) => {
  return http.post(`${BASE}/dispatch/${id}`, {});
};

export const deleteTicket = (id: number | string) => {
  return http.delete(`${BASE}/${id}`);
};

export const bulkUpdateSalePrice = (payload: BulkSalePricePayload) => {
  return http.post(`${BASE}/bulk/sale-price`, payload);
};

export const bulkClear = (payload: HydrovacBulkClearPayload) => {
  return http.post(`${BASE}/bulk/clear`, payload);
};

export const checkBulkUpdate = (payload: HydrovacBulkUpdatePayload) => {
  return http.post(`${BASE}/bulk/check`, payload);
};

export const bulkUpdate = (payload: HydrovacBulkUpdatePayload) => {
  return http.post(`${BASE}/bulk/update`, payload);
};

export const unreceiveTicket = (id: number) => {
  return http.post(`${BASE}/unreceive/${id}`, {});
};

export const getReportingTickets = (payload: GenerateReportPayload) => {
  return http.post(`${BASE}/report`, payload);
};

export const exportTicketsExcel = (payload: GenerateExcelExportPayload) => {
  return http.post(`${BASE}/generate/excel`, payload);
};

export const getTicketLogs = (id: number | string) => {
  return http.get(`${BASE}/logs/${id}`);
};

export default {
  getAll,
  getLatest,
  getReceivedToday,
  getActiveToday,
  getAllComplete,
  getCompleteCount,
  getTicket,
  getTicketHash,
  generateTickets,
  driverSubmit,
  receiveTicket,
  unreceiveTicket,
  clearHeadspace,
  searchTickets,
  getPendingHeadspace,
  saveTicket,
  dispatchTicket,
  deleteTicket,
  bulkUpdateSalePrice,
  bulkClear,
  checkBulkUpdate,
  bulkUpdate,
  getReportingTickets,
  exportTicketsExcel,
  getTicketLogs,
};
